import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import useHackathon from '@/data/hook/hackathon/useHackathon';
import { StageTypeEnum } from '@/models/Hackathon';
import { Project } from '@/models/Project';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Fragment, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { useUpvoting, useVoteStyles } from './Interactions.hooks';

type HackathonVotingProps = {
  project: Project;
  onMutate?: (project: Project) => void;
};

export default function HackathonVotingAction({
  project,
  onMutate,
}: HackathonVotingProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'hackathon',
  });
  const { isLoading, currentStage } = useHackathon();
  const { upVote, isUpvoting } = useUpvoting(project, onMutate);
  const { upVoted } = project;

  const { color, text, Icon } = useVoteStyles(upVoted);

  const handleClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    e.preventDefault();
    upVote();
  };
  if (!currentStage) return <Fragment />;
  const isVotingStage = currentStage.type === StageTypeEnum.INITIAL_VOTING;
  const isSubmissionStage = currentStage.type === StageTypeEnum.SUBMISSION;
  const lockTooltip = isSubmissionStage ? t('votingSoon') : t('votingClosed');
  return (
    <ConditionalWrapper
      wrapper={<Tooltip text={lockTooltip} />}
      condition={!isVotingStage}
    >
      <MainButton
        dataTestId="hackathonVote"
        color="custom"
        className={twJoin('flex-row-reverse gap-1 font-600 !text-14', color)}
        text={text}
        icon={isVotingStage ? <Icon /> : <LockClosedIcon />}
        onClick={handleClick}
        loading={isUpvoting || isLoading}
        disabled={!isVotingStage}
      />
    </ConditionalWrapper>
  );
}
